import React from "react";
import {autorun} from "mobx";
import {observer} from "mobx-react";

import "./sketch.scss";

import EsriSketch from "@arcgis/core/widgets/Sketch";
import EsriSketchViewModel from "@arcgis/core/widgets/Sketch/SketchViewModel";
import EsriGraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import {SketchConfiguration} from "../../config/sketch-configuration";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import {IAppStore, withStore} from "../../stores/abstract-app-store";

interface SketchProps {
    config: SketchConfiguration;
    onLoad?: (sketchViewModel: EsriSketchViewModel) => any;
    store?: IAppStore; // this is injected by withStore
}

@observer
class SketchBase extends React.Component<SketchProps, any> {
    watchHandles: any[] = [];
    autorunDisposers: any[] = [];
    position: string;
    layer: __esri.GraphicsLayer ;
    layerName: string;
    availableCreateTools = ["point", "polyline", "polygon", "rectangle", "circle"];
    disableUpdate = false;
    sketch: EsriSketch;
    store: IAppStore;

    constructor(props: SketchProps) {
        super(props);
        this.position = this.props.config.position || "top-left";
        this.state = {
            sketch: null,
            layer: null
        };
        if (this.props.store?.appStore) {
            // set default create tools
            this.props.store.appStore.setSketchCreateTools(
                this.availableCreateTools.filter((tool: string) => {
                    const {createTools} = this.props.config.visibleElements;
                    return typeof createTools === "undefined"
                        || createTools[tool] || typeof createTools[tool] === "undefined";
                }));
            this.props.store.appStore.setMaxObject(this.props.config.maxObject);
        }
        this.layerName = "sketch";
    }

    componentDidMount(): void {
        const {config, store} = this.props;
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun(() => {
            if (!store) {
                return;
            }
            this.store = store;
            const {
                maxObject,
                mode,
                sketchComplete,
                sketchCreateTools, sketchSelectTools, graphicsCount} = store.appStore;

            if (!config || !store.mapStore.view) {
                return;
            }
            if (mode !== "sketch") {
                this.disable();
                this.setState({
                    sketch: null,
                    layer: null
                });
            } else {
                if(this.watchHandles.length === 0) {
                    this.watchHandles.push(reactiveUtils.when(
                        () => !store.mapStore.view?.updating,
                        () => {
                            this.layer = this.prepareSketchLayer(store.mapStore.view);
                            if (!this.sketch) {
                                this.sketch = this.loadSketch(store.mapStore.view, this.layer, config);
                                if (config.symbols?.activeFill) {
                                    this.sketch.viewModel.set("activeFillSymbol", config.symbols.activeFill);
                                }
                                if (config.symbols?.polygon) {
                                    this.sketch.viewModel.set("polygonSymbol", config.symbols.polygon);
                                }
                                if (config.symbols?.polyline) {
                                    this.sketch.viewModel.set("polylineSymbol", config.symbols.polyline);
                                }
                                store.uiStore.addWidget(this.layerName, this);
                                store.mapStore.view.ui.add(this.sketch, this.position);
                            } else {
                                if (this.sketch.activeTool && !sketchComplete) {
                                    // Zweiter autorun durchlauf durch sketch.cancel() unterdrücken.
                                    this.disableUpdate = true;
                                    this.sketch.cancel();
                                }
                            }
                            this.updateSettings(store, sketchComplete, graphicsCount, maxObject,
                                sketchCreateTools, sketchSelectTools);
                            this.watchHandles.push(
                                this.sketch.viewModel.on("create", (evt: __esri.SketchViewModelCreateEvent) => {
                                    if (evt.state === "complete") {
                                        if (!this.disableUpdate) {
                                            store.appStore.addGraphic(this.layerName, evt.graphic);
                                        }
                                        this.disableUpdate = false;
                                    }
                                })
                            );
                            this.watchHandles.push(
                                this.sketch.viewModel.on("update", (evt: __esri.SketchViewModelUpdateEvent) => {
                                    if (evt.state === "complete") {
                                        if (!this.disableUpdate) {
                                            store.appStore.resetGraphics(this.layerName, this.layer.graphics.toArray());
                                        }
                                        this.disableUpdate = false;
                                    } else if (evt.state === "active") {
                                        // kann für live update der Labels verwendet werden
                                        store.appStore.updateGraphicLabel(this.layerName, evt.graphics);
                                    }
                                })
                            );
                        },
                        { initial: true, once: true }
                    ));
                }else{
                    this.updateSettings(store, sketchComplete, graphicsCount, maxObject,
                        sketchCreateTools, sketchSelectTools)
                }
            }
        }));
    }
    updateSettings(store: IAppStore,
                   sketchComplete: boolean,
                   graphicsCount: number,
                   maxObject: number,
                   sketchCreateTools: string[], sketchSelectTools: string[]): void {
        if (sketchComplete) {
            this.sketch.complete();
            store.appStore.completeSketch(false);
        }
        if (graphicsCount < maxObject){
            this.setCreateTools(this.sketch, sketchCreateTools);
        } else {
            const tools: any = {};
            this.sketch.availableCreateTools.forEach((v: string) => {
                tools[v]=false;
            });
            this.sketch.visibleElements.createTools = tools;
        }
        this.setSelectionTools(this.sketch, sketchSelectTools);

        this.setState({
            sketch: this.sketch,
            layer: this.layer
        });
    }

    prepareSketchLayer(view: __esri.MapView | __esri.SceneView): __esri.GraphicsLayer {
        let layer = view.map.layers.find(
            (l: __esri.GraphicsLayer) => l.title === this.layerName) as __esri.GraphicsLayer;
        if (!layer) {
            layer = new EsriGraphicsLayer({title: this.layerName});
            view.map.layers.push(layer);
        }
        return layer;
    }

    disable(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;

        if (this.store?.mapStore.view) {
            this.store.mapStore.view.ui.remove(this.sketch);
            this.store.mapStore.view.map.remove(this.layer);
            const textLayer = this.store.mapStore.view.map.layers.find(
                (l: __esri.GraphicsLayer) => l.title === this.layerName + "_text") as __esri.GraphicsLayer;
            if (textLayer) {
                this.store.mapStore.view.map.remove(textLayer);
            }
        }
        this.store?.uiStore.removeWidget(this.layerName);
        delete this.sketch;
        delete this.layer;
    }

    componentWillUnmount(): void {
        this.disable();
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
    }

    loadSketch(view: __esri.MapView | __esri.SceneView,
               layer: __esri.GraphicsLayer, config: __esri.SketchProperties): EsriSketch {
        const props = {
            view,
            layer,
            ...config
        };
        return new EsriSketch(props);
    }

    /**
     * Turn on or off sketch create tool according a list of tool names.
     *
     * @param toolNames: The List of tool names.
     * @param sketch: The sketch widget
     */
    setCreateTools(sketch: __esri.Sketch, toolNames: string[]): void {
        if (toolNames.length !== 0 && sketch && sketch.visibleElements) {
            this.availableCreateTools.forEach((tool: string) => {
                if(sketch.visibleElements.createTools) {
                    sketch.visibleElements.createTools[tool] = toolNames.includes(tool);
                }
            });
        }
    }

    /**
     * Turn on or off sketch selection tools according a list of tool names.
     *
     * @param toolNames: The List of tool names.
     * @param sketch: The sketch widget
     */
    setSelectionTools(sketch: __esri.Sketch, toolNames: string[]): void {
        if (toolNames.length !== 0 && sketch && sketch.visibleElements && sketch.visibleElements.selectionTools) {
            sketch.visibleElements.selectionTools["lasso-selection"] = toolNames.includes("lasso-selection");
            sketch.visibleElements.selectionTools["rectangle-selection"] = toolNames.includes("rectangle-selection");
        }
    }

    /**
     * Renders the sketch widget. It is only visible in sketch mode
     */
    render(): null {
        const { sketch } = this.state;
        if (sketch) {
            sketch.renderNow();
        }
        return null;
    }
}

export const Sketch = withStore(SketchBase);
